import { Alert, Button, Space, Spin } from "antd"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useState } from "react"

import Flex from "#blocks/Flex"
import SessionGrid from "#components/forms/AddClass/SessionGrid"
import {
  ATTENDANCE_STATUSES,
  VALID_SWITCH_STATUSES,
} from "#constants/globalConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { AttendanceClassSessionWithDate } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceClassSession, AttendanceStatus } from "#customTypes/types"
import { generateSessionDates } from "#utils/classHelpers"
import { getNow, isTodayOrFuture, setTimeFromString } from "#utils/dateHelpers"
import {
  getBlankAttendance,
  remapAbsentForFutureSessions,
} from "#utils/studentHelpers"

type SessionMatch = {
  oldIndex: number
  newIndex: number
  oldSession: AttendanceClassSessionWithDate
  newSession: ClassSessionConstructor | null
  oldStatus: AttendanceStatus
  newStatus: AttendanceStatus | null
  canSwitch: boolean
  isBeforeSwitchDate: boolean
  reason?: string
}

const SwitchClassSessionGrid = ({
  oldClassAttendance,
  newClassAttendance,
  newClassOppositeAttendance,
  oldClassObj,
  oldClassSessions,
  newClassId,
  newClassObj,
  newClassSessions,
  switchDate,
  onSelectionChange,
  isAssistant,
}: {
  studentId: Uuid
  oldClassAttendance: AttendanceClassSession[]
  newClassAttendance: AttendanceClassSession[]
  newClassOppositeAttendance: AttendanceClassSession[]
  oldClassId: Uuid
  oldClassObj: ClassConstructor
  oldClassSessions: ClassSessionConstructor[]
  newClassId: Uuid | null
  newClassObj: ClassConstructor | null
  newClassSessions: ClassSessionConstructor[]
  switchDate: dayjs.Dayjs
  onSelectionChange: (selectedIndices: boolean[]) => void
  isAssistant: boolean
}) => {
  const [selectedIndices, setSelectedIndices] = useState<boolean[]>([])

  // Both classes have the same curriculum, so they should have the same number of sessions
  const doSessionsMatch = oldClassSessions.length === newClassSessions.length

  const oldClassSessionDates = useMemo(() => {
    if (!oldClassObj) return []

    return generateSessionDates({
      startDate: dayjs(oldClassObj.startDate),
      times: [
        setTimeFromString(oldClassObj.startDate, oldClassObj.startTime)!,
        setTimeFromString(oldClassObj.startDate, oldClassObj.endTime)!,
      ],
      isSummerCamp: oldClassObj.isSummerCamp ?? false,
      numberOfSessions: oldClassObj?.sessionIds?.length ?? 0,
    })
  }, [oldClassObj])

  // Prefill attendanceSessions with blankAttendance for missing data
  // Map over blanks in case sessions got dropped from attendance data
  const filledAttendanceSessionsWithDate: AttendanceClassSessionWithDate[] =
    useMemo(() => {
      if (!oldClassObj) return []

      const blankAttendance = getBlankAttendance(oldClassObj.sessionIds)

      return blankAttendance.map((blankSession, i) => {
        const existingSession = oldClassAttendance?.[i]
        const sessionDate = dayjs(oldClassSessionDates[i].eventStart)

        return {
          ...blankSession,
          status:
            remapAbsentForFutureSessions(
              existingSession.status,
              sessionDate,
              getNow()
            ) ?? blankSession.status,
          sessionDate,
        }
      })
    }, [oldClassAttendance, oldClassSessionDates, oldClassObj])

  // Create session matches by index
  // Show all sessions immediately, even if new class sessions are still loading
  const sessionMatches = useMemo((): SessionMatch[] => {
    if (!newClassId) return []

    const matches: SessionMatch[] = []

    // Use expected session count to show all sessions immediately
    const sessionCount = filledAttendanceSessionsWithDate.length

    for (let i = 0; i < sessionCount; i++) {
      const oldSessionAttendance = filledAttendanceSessionsWithDate[i]
      if (!oldSessionAttendance) break

      const oldStatus = oldSessionAttendance.status
      const newSession = newClassSessions[i] || null
      // Get actual attendance status from new class, or calculate based on old class enrollment
      // If student is already enrolled in new class, use that status
      // Otherwise, if they were enrolled in old class, they become enrolled in new class
      const existingNewClassStatus = newClassAttendance?.[i]?.status
      const newStatus =
        existingNewClassStatus ?? ATTENDANCE_STATUSES.NOT_ENROLLED

      // Remap ABSENT to ENROLLED for future sessions
      const now = getNow().startOf("day")
      const remappedNewStatus = newSession
        ? remapAbsentForFutureSessions(
            newStatus,
            dayjs(newSession.eventStart),
            now
          )
        : newStatus

      // Check if session is before switch date
      const isBeforeSwitchDate = oldSessionAttendance.sessionDate.isBefore(
        switchDate,
        "day"
      )

      // Check if session can be switched
      let canSwitch = true
      let reason: string | undefined

      // Lock sessions where student is not enrolled
      if (oldStatus === ATTENDANCE_STATUSES.NOT_ENROLLED) {
        canSwitch = false
        reason = "Student is not enrolled in this session"
      }

      if (!VALID_SWITCH_STATUSES.includes(oldStatus)) {
        canSwitch = false
        reason = "Student has already attended this session"
      }

      if (!VALID_SWITCH_STATUSES.includes(remappedNewStatus)) {
        canSwitch = false
        reason = "Student already has attendance in new class session"
      }

      // Lock sessions where student has opposite attendance type in the new class:
      // - If switching assistant attendance: check if they're a regular student
      // - If switching regular attendance: check if they're a TA
      const oppositeAttendanceStatus = newClassOppositeAttendance?.[i]?.status
      const remappedOppositeStatus =
        newSession && oppositeAttendanceStatus
          ? remapAbsentForFutureSessions(
              oppositeAttendanceStatus,
              dayjs(newSession.eventStart),
              now
            )
          : oppositeAttendanceStatus
      if (
        remappedOppositeStatus &&
        remappedOppositeStatus !== ATTENDANCE_STATUSES.NOT_ENROLLED
      ) {
        canSwitch = false
        reason = isAssistant
          ? "Student is enrolled as a student in this session"
          : "Student is a TA in this session"
      }

      matches.push({
        oldIndex: i,
        newIndex: i,
        oldSession: oldSessionAttendance,
        newSession,
        oldStatus,
        newStatus: remappedNewStatus,
        canSwitch,
        isBeforeSwitchDate,
        reason,
      })
    }

    return matches
  }, [
    filledAttendanceSessionsWithDate,
    newClassSessions,
    newClassId,
    switchDate,
    newClassAttendance,
    newClassOppositeAttendance,
    isAssistant,
  ])

  // Calculate warnings
  const warnings = useMemo(() => {
    const warningsList: string[] = []
    const cannotSwitch = sessionMatches.filter((m) => !m.canSwitch)

    if (!doSessionsMatch) {
      warningsList.push(
        "The old class has a different number of sessions than the new class"
      )
    }

    if (cannotSwitch.length === sessionMatches.length) {
      warningsList.push("No sessions can be switched")
    } else if (cannotSwitch.length > 0) {
      warningsList.push(
        `${cannotSwitch.length} session${cannotSwitch.length > 1 ? "s" : ""} cannot be switched`
      )
    }

    return warningsList
  }, [sessionMatches, doSessionsMatch])

  const handleSessionClick = useCallback(
    (index: number) => {
      const match = sessionMatches[index]
      if (!match) return
      // Block if it can't switch (makeups, actual attendance, etc.) regardless of date
      if (!match.canSwitch) return

      setSelectedIndices((prev) => {
        const newSelection = [...prev]
        newSelection[index] = !newSelection[index]
        return newSelection
      })
    },
    [sessionMatches]
  )

  // Sync selection changes to parent
  useEffect(() => {
    onSelectionChange(selectedIndices)
  }, [selectedIndices, onSelectionChange])

  const handleSelectAll = useCallback(() => {
    // Only select sessions that can switch
    const newSelection = sessionMatches.map((m) => m.canSwitch)
    setSelectedIndices(newSelection)
  }, [sessionMatches])

  const handleSelectAllFuture = useCallback(() => {
    setSelectedIndices((prev) => {
      const newSelection = [...prev]
      sessionMatches.forEach((m, i) => {
        if (m.canSwitch) {
          const sessionDate = dayjs(
            m.newSession?.eventStart || m.oldSession.sessionDate
          )
          if (isTodayOrFuture(sessionDate, getNow())) {
            newSelection[i] = true
          }
        }
      })
      return newSelection
    })
  }, [sessionMatches])

  // Auto-select all valid future sessions when newClassId changes or sessionMatches first become available
  useEffect(() => {
    if (sessionMatches.length === 0 || !newClassId) {
      setSelectedIndices([])
      return
    }
    handleSelectAllFuture()
  }, [sessionMatches, newClassId, handleSelectAllFuture])

  const handleRemoveAll = useCallback(() => {
    setSelectedIndices([])
  }, [])

  const handleRemoveAllFuture = useCallback(() => {
    setSelectedIndices((prev) => {
      const newSelection = [...prev]
      sessionMatches.forEach((m, i) => {
        if (prev[i]) {
          const sessionDate = dayjs(
            m.newSession?.eventStart || m.oldSession.sessionDate
          )
          if (isTodayOrFuture(sessionDate, getNow())) {
            newSelection[i] = false
          }
        }
      })
      return newSelection
    })
  }, [sessionMatches])

  // Always show current class structure
  // Show loading or placeholder for new class if not selected/loading
  const showNewClassGrid = newClassId != null

  return (
    <Flex col gap={20}>
      {warnings.length > 0 && (
        <Flex col gap={5}>
          {warnings.map((warning, i) => (
            <Alert key={i} message={warning} type="warning" showIcon />
          ))}
        </Flex>
      )}

      <Space
        style={{
          margin: "10px 20px 20px 0",
        }}
      >
        <Button variant="outlined" onClick={handleSelectAll}>
          Select all
        </Button>
        <Button variant="outlined" onClick={handleSelectAllFuture}>
          Select all future
        </Button>
        <Button danger onClick={handleRemoveAll}>
          Remove all
        </Button>
        <Button danger onClick={handleRemoveAllFuture}>
          Remove all future
        </Button>
      </Space>

      <Flex gap={100} style={{ alignItems: "flex-start" }}>
        {/* Old Class */}
        <Flex col gap={10}>
          <h3>
            Current Class{oldClassObj?.name ? ` - ${oldClassObj.name}` : ""}
            {selectedIndices.filter(Boolean).length > 0 && (
              <span style={{ fontWeight: "normal", color: "#666" }}>
                {" "}
                ({selectedIndices.filter(Boolean).length}{" "}
                {selectedIndices.filter(Boolean).length === 1
                  ? "session"
                  : "sessions"}{" "}
                selected)
              </span>
            )}
          </h3>
          <SessionGrid
            items={sessionMatches.map((match, i) => {
              const isSelected = selectedIndices[i] ?? false
              // Disabled if it can't switch (makeups, actual attendance, etc.) regardless of date
              const isDisabled = !match.canSwitch
              // Past sessions are visually styled differently (grayed out)
              // Mark as past if it's before switch date (regardless of disabled state)
              const isPast = match.isBeforeSwitchDate

              // Sessions are sequential, so we can access by index directly
              const oldSession = oldClassSessions[i]

              return {
                sessionId: oldSession?.id ?? match.oldSession.sessionId,
                status: match.oldStatus,
                isSelected,
                isDisabled,
                isPast,
                disabledReason: match.reason,
                onClick: () => handleSessionClick(i),
                key: `old-${i}`,
              }
            })}
          />
        </Flex>

        {/* New Class */}
        <Flex col gap={10}>
          <h3>New Class{newClassObj?.name ? ` - ${newClassObj.name}` : ""}</h3>
          {showNewClassGrid ? (
            <SessionGrid
              items={sessionMatches.map((match, i) => {
                const isSelected = selectedIndices[i] ?? false
                // Disabled if it can't switch (makeups, actual attendance, etc.) regardless of date
                const isDisabled = !match.canSwitch
                // Past sessions are visually styled differently (grayed out)
                // Mark as past if it's before switch date (regardless of disabled state)
                const isPast = match.isBeforeSwitchDate

                // If session is loaded, show it
                if (match.newSession) {
                  return {
                    sessionId: match.newSession.id,
                    status: match.newStatus || ATTENDANCE_STATUSES.NOT_ENROLLED,
                    isSelected,
                    isDisabled,
                    isPast,
                    disabledReason: match.reason,
                    onClick: () => handleSessionClick(i),
                    key: `new-${i}`,
                  }
                }

                // If session is not loaded yet, return null to trigger renderEmptySlot
                return null
              })}
              renderEmptySlot={(index) => {
                const match = sessionMatches[index]
                // If session is loaded, don't render empty slot
                if (match?.newSession) return null

                // Show loading state if all data is not yet fulfilled
                const isLoading = newClassObj != null

                return (
                  <Flex
                    center
                    style={{
                      width: 106,
                      height: 74,
                      border: "2px dashed #ccc",
                      borderRadius: 6,
                      color: "#999",
                    }}
                  >
                    {isLoading ? <Spin size="small" /> : "No session"}
                  </Flex>
                )
              }}
            />
          ) : (
            <Flex
              center
              style={{
                width: "100%",
                minHeight: 200,
                border: "2px dashed #ddd",
                borderRadius: 6,
                padding: 40,
                color: "#999",
              }}
            >
              <p style={{ margin: 0 }}>Please select a class to switch to</p>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default SwitchClassSessionGrid
